import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Card, EmptyState } from './Shared';
import { patientProfileAPI } from '../services/api';

/**
 * PatientReferralLettersTab
 *
 * Renders the list of referral letters issued for a given patient.
 * Each letter shows: consultation date+time, doctor, specialty referred,
 * email status, version info if revised, and a button to open the PDF.
 *
 * The PDF is fetched through the backend proxy (auth + audit logging).
 *
 * Props:
 *   patientId — string (UUID)
 */
export default function PatientReferralLettersTab({ patientId }) {
  const [letters, setLetters] = useState(null); // null = loading, [] = loaded empty
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!patientId) return;

    (async () => {
      try {
        const res = await patientProfileAPI.getReferralLetters(patientId);
        if (cancelled) return;
        setLetters(res.data?.data || []);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.error || 'Αποτυχία φόρτωσης παραπεμπτικών';
        setError(msg);
        setLetters([]);
      }
    })();

    return () => { cancelled = true; };
  }, [patientId]);

  const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const time = timeStr ? timeStr.slice(0, 5) : '';
    return `${dd}/${mm}/${yyyy}${time ? ' ' + time : ''}`;
  };

  const formatSentAt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  };

  const handleOpenPdf = async (letterId) => {
    try {
      const res = await patientProfileAPI.getReferralLetterPdfBlob(letterId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        toast.error('Ο browser απέκλεισε το αναδυόμενο παράθυρο. Ενεργοποιήστε τα popups για αυτή τη σελίδα.');
        URL.revokeObjectURL(url);
        return;
      }
      // Free the blob URL after a delay to allow the new tab to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Αποτυχία λήψης παραπεμπτικού';
      toast.error(msg);
    }
  };

  if (letters === null) {
    return (
      <Card title="📨 Παραπεμπτικά">
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>Φόρτωση...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="📨 Παραπεμπτικά">
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--danger, #c00)' }}>{error}</div>
      </Card>
    );
  }

  if (letters.length === 0) {
    return (
      <Card title="📨 Παραπεμπτικά">
        <EmptyState
          icon="📨"
          title="Καμία παραπομπή"
          subtitle="Ο ασθενής δεν έχει λάβει παραπεμπτικό μέχρι στιγμής."
        />
      </Card>
    );
  }

  return (
    <Card title={`📨 Παραπεμπτικά (${letters.length})`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {letters.map((l) => {
          const doctorName = `Δρ. ${l.doctor_first_name || ''} ${l.doctor_last_name || ''}`.trim();
          const consDateTime = formatDateTime(l.appointment_date, l.start_time);
          const sentAt = formatSentAt(l.email_sent_at);
          const isRevised = l.is_revised === true;
          const isCurrent = !isRevised;
          return (
            <div
              key={l.id}
              style={{
                padding: 16,
                border: '1px solid var(--border)',
                borderRadius: 8,
                background: isCurrent ? 'var(--bg, #fafafa)' : '#f5f5f5',
                opacity: isCurrent ? 1 : 0.7,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {consDateTime} · {doctorName}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--text)', marginBottom: 4 }}>
                    Παραπομπή σε: <strong>{l.referral_to_specialty}</strong>
                    {l.version > 1 && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--muted)' }}>
                        (έκδοση v{l.version}{isRevised ? ', αναθεωρημένο' : ''})
                      </span>
                    )}
                    {l.version === 1 && isRevised && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--muted)' }}>
                        (αρχική έκδοση, αναθεωρημένο)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {sentAt ? (
                      <>Στάλθηκε στις {sentAt}{l.email_recipient ? ` στο ${l.email_recipient}` : ''}</>
                    ) : (
                      <span style={{ color: 'var(--danger, #c00)' }}>Δεν στάλθηκε email</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleOpenPdf(l.id)}
                  style={{
                    flexShrink: 0,
                    background: 'var(--crimson, #C0185A)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  📄 Λήψη PDF
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
